import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, FormGroup, Input, Label, Row, Spinner, Table } from "reactstrap";
import { AUTOPILOT_COLORS } from "../../../components/autopilot/styles";
import { useToast } from "../../../hooks/toast";
import {
  adminListarContatos,
  adminCriarContato,
  adminAtualizarContato,
  mensagemErro,
} from "../../../services/meuFreteApi";

// Aba Contatos do cadastro da transportadora (PO1-3492, admin Everlog). O contato
// é gravado por embarcador; aqui ele aparece uma vez por pessoa (mesmo e-mail +
// tipo), com "Ver embarcadores" mostrando quais embarcadores têm o contato e
// quais da carteira ainda não têm. Editar vale para todos (regra da PO1-2308).
// Sem excluir: a regra da PO1-2308 é não apagar contato.

const VAZIO = { idTipoContato: "", nome: "", email: "", dddTelefone: "", telefone: "" };

const mutedStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.78rem" };
const chipStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "0.1rem 0.55rem",
  borderRadius: 999,
  fontSize: "0.74rem",
  background: AUTOPILOT_COLORS.insetSurface,
  border: `1px solid ${AUTOPILOT_COLORS.border}`,
  color: AUTOPILOT_COLORS.text,
  marginRight: 6,
  marginBottom: 6,
};
const linkStyle = { background: "none", border: 0, padding: 0, color: AUTOPILOT_COLORS.accent, cursor: "pointer", fontSize: "0.78rem" };
const painelStyle = {
  border: `1px solid ${AUTOPILOT_COLORS.border}`,
  borderRadius: 8,
  padding: "1rem",
  marginBottom: "1rem",
  background: AUTOPILOT_COLORS.insetSurface,
};

export function telefoneDe(c) {
  const fone = c.telefone || c.celular;
  if (!fone) return "";
  const ddd = c.telefone ? c.dddTelefone : c.dddCelular;
  return ddd ? `(${ddd}) ${fone}` : String(fone);
}

// Embarcadores da carteira que ainda não têm este contato.
export function faltandoNaCarteira(contato, carteira) {
  const tem = new Set((contato.embarcadores || []).map((e) => Number(e.idEmbarcador)));
  return (carteira || []).filter((e) => !tem.has(Number(e.id)));
}

export default function ContatosTab({ transportadoraId, onChanged }) {
  const { addToast } = useToast();
  const [data, setData] = useState({ tipos: [], carteira: [], contatos: [] });
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [abertos, setAbertos] = useState({});
  // form: { modo: "novo" | "editar", id?, qtdEmbarcadores?, dados, embarcadores: [] }
  const [form, setForm] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    if (!transportadoraId) return;
    setLoading(true);
    setErro(null);
    try {
      setData(await adminListarContatos(transportadoraId));
    } catch (err) {
      setErro(mensagemErro(err, "Não foi possível carregar os contatos."));
    } finally {
      setLoading(false);
    }
  }, [transportadoraId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const aplicar = (out, titulo, descricao) => {
    setData({ tipos: out.tipos || data.tipos, carteira: out.carteira || data.carteira, contatos: out.contatos || [] });
    addToast({ place: "tr", type: "success", title: titulo, description: descricao });
    if (onChanged) onChanged();
  };

  const novo = () => {
    setForm({ modo: "novo", dados: { ...VAZIO }, embarcadores: data.carteira.map((e) => Number(e.id)) });
  };

  const editar = (c) => {
    setForm({
      modo: "editar",
      id: c.id,
      qtdEmbarcadores: c.email ? c.embarcadores.length : 1,
      dados: { idTipoContato: String(c.idTipoContato || ""), nome: c.nome || "", email: c.email || "", dddTelefone: c.dddTelefone || "", telefone: c.telefone || "" },
      embarcadores: [],
    });
  };

  const campo = (nome, valor) => setForm((f) => ({ ...f, dados: { ...f.dados, [nome]: valor } }));

  const alternarEmbarcador = (id) =>
    setForm((f) => ({
      ...f,
      embarcadores: f.embarcadores.indexOf(id) >= 0 ? f.embarcadores.filter((x) => x !== id) : f.embarcadores.concat(id),
    }));

  const salvar = async () => {
    setSalvando(true);
    try {
      const dados = { ...form.dados, idTipoContato: form.dados.idTipoContato ? Number(form.dados.idTipoContato) : undefined };
      if (form.modo === "novo") {
        const out = await adminCriarContato(transportadoraId, { dados, embarcadores: form.embarcadores });
        aplicar(out, "Contato cadastrado", `${dados.nome.trim()}: incluído em ${out.criados} embarcador(es).`);
      } else {
        const out = await adminAtualizarContato(transportadoraId, form.id, dados);
        aplicar(out, "Contato atualizado", `Alterado em ${out.atualizados} embarcador(es).`);
      }
      setForm(null);
    } catch (err) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível salvar o contato", description: mensagemErro(err) });
    } finally {
      setSalvando(false);
    }
  };

  // Inclui um contato existente em mais um embarcador da carteira.
  const incluirEm = async (c, emb) => {
    setSalvando(true);
    try {
      const dados = { idTipoContato: c.idTipoContato, nome: c.nome, email: c.email, dddTelefone: c.dddTelefone, telefone: c.telefone, dddCelular: c.dddCelular, celular: c.celular };
      const out = await adminCriarContato(transportadoraId, { dados, embarcadores: [Number(emb.id)] });
      aplicar(out, "Contato incluído", `${c.nome} agora também em ${emb.nome}.`);
    } catch (err) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível incluir", description: mensagemErro(err) });
    } finally {
      setSalvando(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: "center", padding: "2rem" }}><Spinner color="info" /></div>;
  }
  if (erro) {
    return <div style={{ color: AUTOPILOT_COLORS.statusDanger, padding: "1rem" }}>{erro}</div>;
  }

  const podeSalvar = form && form.dados.nome.trim() && (form.modo === "editar" || (form.dados.idTipoContato && form.embarcadores.length));

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
        <span style={mutedStyle}>
          {data.contatos.length} contato(s). Cada contato vale para os embarcadores listados nele.
        </span>
        {!form && (
          <Button size="sm" color="info" onClick={novo} disabled={!data.carteira.length} title={data.carteira.length ? "" : "A transportadora não está na carteira de nenhum embarcador"}>
            Novo contato
          </Button>
        )}
      </div>

      {form && (
        <div style={painelStyle}>
          <div style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600, marginBottom: "0.75rem" }}>
            {form.modo === "novo" ? "Novo contato" : "Editar contato"}
          </div>
          <Row>
            <Col md="3">
              <FormGroup>
                <Label for="contato-tipo">Tipo *</Label>
                <Input id="contato-tipo" type="select" value={form.dados.idTipoContato} disabled={form.modo === "editar"} onChange={(e) => campo("idTipoContato", e.target.value)}>
                  <option value="">Selecione…</option>
                  {data.tipos.map((t) => (
                    <option key={t.id} value={String(t.id)}>{t.nome}</option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label for="contato-nome">Nome *</Label>
                <Input id="contato-nome" maxLength={60} value={form.dados.nome} onChange={(e) => campo("nome", e.target.value)} />
              </FormGroup>
            </Col>
            <Col md="5">
              <FormGroup>
                <Label for="contato-email">E-mail</Label>
                <Input id="contato-email" type="email" maxLength={100} value={form.dados.email} onChange={(e) => campo("email", e.target.value)} />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="2">
              <FormGroup>
                <Label for="contato-ddd">DDD</Label>
                <Input id="contato-ddd" maxLength={4} value={form.dados.dddTelefone} onChange={(e) => campo("dddTelefone", e.target.value)} />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label for="contato-telefone">Telefone</Label>
                <Input id="contato-telefone" maxLength={20} value={form.dados.telefone} onChange={(e) => campo("telefone", e.target.value)} />
              </FormGroup>
            </Col>
          </Row>
          {form.modo === "novo" ? (
            <FormGroup>
              <Label>Embarcadores *</Label>
              <div>
                {data.carteira.map((e) => (
                  <label key={e.id} style={{ ...chipStyle, cursor: "pointer" }}>
                    <input type="checkbox" checked={form.embarcadores.indexOf(Number(e.id)) >= 0} onChange={() => alternarEmbarcador(Number(e.id))} style={{ margin: 0 }} />
                    {e.nome}
                  </label>
                ))}
              </div>
            </FormGroup>
          ) : (
            <div style={{ ...mutedStyle, marginBottom: "0.75rem" }}>
              A alteração vale para {form.qtdEmbarcadores} embarcador(es) com este contato.
            </div>
          )}
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <Button size="sm" color="info" onClick={salvar} disabled={!podeSalvar || salvando}>
              {salvando ? <Spinner size="sm" /> : "Salvar"}
            </Button>
            <Button size="sm" color="secondary" outline onClick={() => setForm(null)} disabled={salvando}>Cancelar</Button>
          </div>
        </div>
      )}

      {!data.contatos.length ? (
        <div style={{ ...mutedStyle, padding: "1rem 0" }}>Nenhum contato cadastrado.</div>
      ) : (
        <Table size="sm" responsive className="hub-thead-accent">
          <thead className="text-primary">
            <tr>
              <th>Tipo</th>
              <th>Nome</th>
              <th>E-mail</th>
              <th>Telefone</th>
              <th>Embarcadores</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.contatos.map((c) => {
              const aberto = !!abertos[c.chave];
              const faltando = faltandoNaCarteira(c, data.carteira);
              return (
                <React.Fragment key={c.chave}>
                  <tr>
                    <td>{c.tipoContato || "—"}</td>
                    <td style={{ color: AUTOPILOT_COLORS.title }}>{c.nome}</td>
                    <td>{c.email || <span style={mutedStyle}>sem e-mail</span>}</td>
                    <td>{telefoneDe(c) || <span style={mutedStyle}>—</span>}</td>
                    <td>
                      <button type="button" style={linkStyle} onClick={() => setAbertos((a) => ({ ...a, [c.chave]: !aberto }))}>
                        {aberto ? "Ocultar embarcadores" : `Ver embarcadores (${c.embarcadores.length})`}
                      </button>
                    </td>
                    <td className="text-right">
                      <Button size="sm" color="link" onClick={() => editar(c)} disabled={!!form}>Editar</Button>
                    </td>
                  </tr>
                  {aberto && (
                    <tr>
                      <td colSpan={6} style={{ background: AUTOPILOT_COLORS.insetSurface }}>
                        <div style={{ ...mutedStyle, marginBottom: 6 }}>Têm este contato:</div>
                        {c.embarcadores.map((e) => (
                          <span key={e.idContato} style={chipStyle}>{e.nome}</span>
                        ))}
                        {c.email && faltando.length > 0 && (
                          <>
                            <div style={{ ...mutedStyle, margin: "8px 0 6px" }}>Na carteira, ainda sem este contato:</div>
                            {faltando.map((e) => (
                              <span key={e.id} style={chipStyle}>
                                {e.nome}
                                <button type="button" style={linkStyle} disabled={salvando} onClick={() => incluirEm(c, e)}>+ incluir</button>
                              </span>
                            ))}
                          </>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      )}
    </div>
  );
}
